/**
 * StepAboutYou.js
 *
 * The first step in the onboarding wizard, collecting basic user information
 * such as name and phone number.
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import validators from '@/utils/validators';
import Overlay from '@/forms/_common/Overlay';
import FormErrors from '@/forms/_common/FormErrors';
import RequestError from '@/forms/_common/RequestError';
import ErrorMessage from '@/forms/_common/ErrorMessage';
import FormTemplate from '@/dialogs_common/SetupTemplate';
import { useOnboarding } from '@/pages/setup/account/OnboardingContext';
import AboutYouFields from './AboutYouFields';
import AboutYouFooter from './AboutYouFooter';

StepAboutYou.propTypes = {
  user: PropTypes.object.isRequired,
};

/**
 * The "About You" step in the onboarding wizard.
 *
 * @param {object} _props - Component props.
 * @returns {JSX.Element} The rendered "About You" step.
 */
export default function StepAboutYou(_props) {

  const { saving, loading, error, request, save, refetchUser, registerOnBeforeNext, setNextDisabled, data } = useOnboarding();

  const [aboutData, setAboutData] = useState(_.pick(data, [
    'firstName',
    'lastName',
    'phoneNumber'
  ]));
  const initialAboutData = useRef(aboutData);
  const [showErrors, setShowErrors] = useState(false);

  /**
   * Handles changes to the user information fields.
   *
   * @param {string} name - The field name.
   * @param {string} value - The field value.
   */
  function onChange(name, value) {
    setAboutData(_.merge({}, aboutData, {
      [name]: value
    }));
  }

  useEffect(() => {
    const hasRequired = Boolean(aboutData.firstName) && Boolean(aboutData.lastName);
    setNextDisabled(!hasRequired);
  }, [aboutData, setNextDisabled]);

  useEffect(() => {
    registerOnBeforeNext(async () => {
      const errors = {
        firstName: validators.isRequired(aboutData.firstName),
        lastName: validators.isRequired(aboutData.lastName),
      };
      const hasError = Object.values(errors).some(Boolean);
      if (hasError) {
        setShowErrors(true);
        return Promise.resolve(false);
      }
      if (_.isEqual(aboutData, initialAboutData.current)) {
        return true;
      }
      const success = await save(aboutData);
      if (success) {
        refetchUser();
        return true;
      }
      return false;
    });
  }, [aboutData, registerOnBeforeNext, save, refetchUser]);

  return (
    <div className="mt-6">
      <Overlay isVisible={loading || saving}>
        <FormErrors data={aboutData} validators={{
          firstName: [validators.isRequired],
          lastName: [validators.isRequired]
        }}>
          {({ errors }) => {
            return (
              <FormTemplate
                title="Let's get your account set up!"
                subtitle="What name would you like us to use? Add a phone number if you'd like someone to help you get started."
                alert={error ? (
                  <RequestError request={request} v2={true} />
                ) : showErrors ? (
                  <ErrorMessage v2={true}>
                    Please fill in all required fields.
                  </ErrorMessage>
                ) : null}
                body={<AboutYouFields data={aboutData} errors={showErrors && errors} onChange={onChange} />}
                footer={<AboutYouFooter />}
              />
            );
          }}
        </FormErrors>
      </Overlay>
    </div>
  );
}
